import React from 'react';
import { Badge, Flex, Grid, GridItem } from '@chakra-ui/react';
import { RoutePath, SPACING } from '../../generic/config';
import { Context } from '../../App';
import { getWorksiteAddress, WorksiteModel } from '../../model/worksite';
import { useNavigate } from 'react-router-dom';
import { localStorageKeys } from '../../generic/constants';
import ModelInput from '../../base-components/ModelInput';
import Card from '../../base-components/Card';
import useModel from '../../base-components/hooks/useModel';
import EditorParent from '@/base-components/EditorParent';
import WorksiteMapComponent from '@/base-components/WorksiteMapComponent';
import { Button } from '@/components/ui/button';
import Divider from '@/base-components/Divider';
import ConnectedCustomerBox from '../order/ConnectedCustomerBox';
import Worksite from '../../model/worksite';
import AttachedFilesGridItem from '../order/AttachedFilesGridItem';
import PropertyValuesCard from '@/base-components/PropertyValuesCard';
import { isDesktop } from 'react-device-detect';
import { previewAttachmentFromWorksite } from '@/api/worksite-controller';
import { Translations } from '@/generic/Translations';
import Order from '@/model/order';

const WorksitePage: React.FC = () => {
    const { translations } = React.useContext(Context);
    const { model, toolbar } = useModel<Worksite>(WorksiteModel);
    const navigate = useNavigate();
    const [showMap, setShowMap] = React.useState(false);
    const title = model.object.name || getWorksiteAddress(model.object);
    const description = model.object.externalId ? `${model.object.id} - ${model.object.externalId}` : `${model.object.id}`
    const isDisabled = !model.object.active;

    const onCreateOrder = () => {
        const orderContext: Partial<Order> = {
            worksite: model.object,
        };

        localStorage.setItem(localStorageKeys.editorPersistedContext, JSON.stringify(orderContext));
        navigate(`${RoutePath.Orders}/new`);
    };

    return (
        <EditorParent title={title} titleAddon={<ActiveBadge active={model.object.active} translations={translations} />} description={description} toolbar={toolbar} actions={[
            { title: translations.common[isDisabled ? "enable" : "disable"], onClick: () => model.setObject({ ...model.object, active: !model.object.active }) },
            { title: translations.model.worksite.createOrder, onClick: onCreateOrder },
        ]}>
            <Grid templateColumns={isDesktop ? "repeat(2, 1fr)" : "repeat(1, 1fr)"} gap={SPACING} alignItems="stretch">
                <GridItem>
                    <Card {...translations.model.worksite.cards.baseInfo} height={"100%"}>
                        <ModelInput title={translations.model.worksite.name} fieldKey="name" model={model} />
                        <ModelInput title={translations.model.worksite.externalId} fieldKey="externalId" model={model} />
                        <ModelInput title={translations.model.worksite.address} fieldKey="address" model={model} />
                        <ModelInput title={translations.model.worksite.identifier} fieldKey="identifier" model={model} />
                        <ModelInput title={translations.model.worksite.postalCode} fieldKey="postalCode" model={model} />
                        <ModelInput title={translations.model.worksite.city} fieldKey="city" model={model} />
                    </Card>
                </GridItem>
                <GridItem>
                    <Flex flexDirection="column" gap={SPACING} h="100%">
                        <Card {...translations.model.order.cards.customer}>
                            {model.object.customer && <ConnectedCustomerBox customer={model.object.customer} onConnect={model.isNew ? (customer) => model.setObject({ ...model.object, customer }) : undefined} showTitle={false} />}
                        </Card>
                        <Card {...translations.model.worksite.cards.contactInfo} height={"100%"}>
                            <ModelInput title={translations.model.worksite.contactPerson} fieldKey="contactPerson" model={model} />
                            <ModelInput title={translations.model.worksite.contactEmail} fieldKey="contactEmail" model={model} />
                            <ModelInput title={translations.model.worksite.contactPhone} fieldKey="contactPhone" model={model} />
                            <Divider />
                        </Card>
                    </Flex>
                </GridItem>
                <GridItem>
                    <Card {...translations.model.worksite.cards.notes}>
                        <ModelInput title={translations.model.worksite.notes} fieldKey="notes" model={model} />
                    </Card>
                </GridItem>
                <GridItem>
                    <Card {...translations.model.worksite.cards.map}>
                        {!showMap && <Button my="auto" w="50%" mx="auto" onClick={() => setShowMap(true)}>{translations.model.worksite.cards.map.loadMap}</Button>}
                        {showMap && (
                            <WorksiteMapComponent model={model} />
                        )}
                    </Card>
                </GridItem>
                <GridItem>
                    <PropertyValuesCard
                        objectType="worksite"
                        objectId={model.object.id}
                        values={model.object.propertyValues}
                        onChange={(propertyValues) => model.setObject({ ...model.object, propertyValues })}
                    />
                </GridItem>
                <AttachedFilesGridItem model={model} previewAttachment={previewAttachmentFromWorksite} />
            </Grid>
        </EditorParent>
    );
};

export default WorksitePage;

export const ActiveBadge = ({ active, translations }: { active: boolean; translations: Translations }) => <Badge bg={active ? "green.emphasized" : "red.emphasized"}>{translations.common[active ? "active" : "disabled"]}</Badge>;